import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, input, output, signal } from '@angular/core';
import { MEMBERS_DATA_SERVICE } from '@app/app/page-modules/admin/services/members/members-data.service';
import {
  BooleanQueryType,
  EmailRequest,
  ExternalIdRequest,
  FirstNameRequest,
  LastNameRequest,
  MembersBooleanSearchRequest,
  OrRequest,
} from '@app/app/shared/models/admin/boolean-filters.model';
import { UserInfo } from '@app/app/shared/models/analytics/analytics.model';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { TranslocoModule } from '@ngneat/transloco';
import { Subject, catchError, debounceTime, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';

const SEARCH_DEBOUNCE_MS = 250;
const MAX_RESULTS = 10;

@Component({
  selector: 'ptl-v3-member-search',
  standalone: true,
  imports: [StyleguideModule, TranslocoModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './v3-member-search.component.html',
  styleUrls: ['./v3-member-search.component.scss'],
})
export class V3MemberSearchComponent {
  placeholder = input<string | undefined>(undefined);
  excludedUserUids = input<string[]>([]);
  disabled = input(false);

  memberSelected = output<UserInfo>();

  protected readonly searchText = signal('');
  protected readonly results = signal<UserInfo[]>([]);
  protected readonly loading = signal(false);
  protected readonly searched = signal(false);
  protected readonly open = signal(false);

  private readonly membersDataService = inject(MEMBERS_DATA_SERVICE);
  private readonly searchTerm$ = new Subject<string>();
  private readonly host = inject(ElementRef<HTMLElement>);

  constructor() {
    this.searchTerm$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        tap((term) => {
          this.loading.set(!!term);
          if (!term) {
            this.results.set([]);
            this.searched.set(false);
          }
        }),
        switchMap((term) => (term ? this.search(term) : of(null))),
      )
      .subscribe((members) => {
        if (members === null) return;
        this.results.set(members);
        this.searched.set(true);
        this.loading.set(false);
      });
  }

  protected onSearch(term: string): void {
    if (this.disabled()) return;
    this.searchText.set(term);
    this.open.set(!!term.trim());
    this.searchTerm$.next(term.trim());
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.open.set(false);
  }

  @HostListener('keydown.escape')
  protected onEscape(): void {
    this.open.set(false);
  }

  protected onFocusSearch(): void {
    if (this.searchText().trim()) this.open.set(true);
  }

  protected onPick(member: UserInfo): void {
    if (this.disabled()) return;
    this.memberSelected.emit(member);
    this.onSearch('');
  }

  protected visibleResults(): UserInfo[] {
    const excluded = this.excludedUserUids();
    return this.results().filter((member) => !excluded.includes(member.userId));
  }

  private search(term: string) {
    const firstName: FirstNameRequest = { type: BooleanQueryType.FIRST_NAME, firstName: term };
    const lastName: LastNameRequest = { type: BooleanQueryType.LAST_NAME, lastName: term };
    const externalId: ExternalIdRequest = { type: BooleanQueryType.EXTERNAL_ID, externalId: term };
    const email: EmailRequest = { type: BooleanQueryType.EMAIL, email: term };
    const request: OrRequest = { type: BooleanQueryType.OR, args: [firstName, lastName, externalId, email] };

    return this.membersDataService
      .searchMembers({ query: request, order: { fieldName: 'firstName', direction: 'ASC' } } as MembersBooleanSearchRequest, 0, MAX_RESULTS)
      .pipe(
        map((result) =>
          result.isSuccess
            ? result.value.members.map(
                (member): UserInfo => ({
                  userId: member._id,
                  imageUrl: member.imageUrl,
                  firstName: member.firstName,
                  lastName: member.lastName,
                  email: member.email,
                }),
              )
            : [],
        ),
        catchError(() => of([] as UserInfo[])),
      );
  }
}
